'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MapIcon from '@mui/icons-material/Map';
import TuneIcon from '@mui/icons-material/Tune';
import FilterListIcon from '@mui/icons-material/FilterList';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { ToolCallDisplay } from '../../types/agent.types';
import { TOOL_LABELS } from '../../types/agent.types';
import { triggerEventWithMessage } from '@/lib/events';

interface ToolResultCardProps {
  toolCall: ToolCallDisplay;
}

interface CatalogSearchResult {
  location?: string;
  dateRange?: { start: string; end: string };
  resolution?: string;
  totalResults?: number;
  summary?: string;
  searchParams?: {
    coordinates?: number[][][];
    startDate?: string;
    endDate?: string;
    dataType?: string[];
    locationName?: string;
  };
}

interface SearchRefinerResult {
  action?: string;
  actionParams?: Record<string, unknown>;
  summary?: string;
}

interface CartManagerResult {
  action?: string;
  actionParams?: Record<string, unknown>;
  summary?: string;
}

interface PageNavigatorResult {
  actionParams?: Record<string, unknown>;
  summary?: string;
}

interface OfferingsBrowserResult {
  actionParams?: Record<string, unknown>;
  summary?: string;
  offerings?: unknown[];
  totalCount?: number;
}

// Track which tool calls have been auto-triggered to prevent duplicates on drawer remount.
// Keyed by toolUseId (unique per tool call) — not by payload content.
const triggeredToolCalls = new Set<string>();

/** Clear triggered tool call tracking (call on new chat). */
export function clearTriggeredToolCalls() {
  triggeredToolCalls.clear();
}

/** Mark tool calls as already triggered (call when loading historical messages). */
export function markToolCallsTriggered(toolUseIds: string[]) {
  toolUseIds.forEach(id => triggeredToolCalls.add(id));
}

function CatalogSearchCard({
  result,
  toolUseId,
}: {
  result: CatalogSearchResult;
  toolUseId: string;
}) {
  const hasTriggered = useRef(false);

  useEffect(() => {
    if (hasTriggered.current) return;
    if (!result.searchParams?.coordinates?.length) return;
    if (triggeredToolCalls.has(toolUseId)) return;

    hasTriggered.current = true;
    triggeredToolCalls.add(toolUseId);
    triggerEventWithMessage('agent:trigger-search', result.searchParams);
  }, [result.searchParams, toolUseId]);

  return (
    <Box
      sx={{
        mt: '4px',
        bgcolor: 'var(--tertiary-color)',
        border: '1px solid rgba(244, 246, 255, 0.1)',
        borderRadius: '8px',
        p: '0.625rem 0.75rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
      }}
    >
      <MapIcon sx={{ fontSize: '0.875rem', color: 'var(--primary-color)' }} />
      <Box
        sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '0.375rem', alignItems: 'center' }}
      >
        {result.location && (
          <Typography
            variant="caption"
            sx={{ color: 'var(--sw-white-color)', fontSize: '0.75rem' }}
          >
            {result.location}
          </Typography>
        )}
        {result.location && result.dateRange && (
          <Typography
            variant="caption"
            sx={{ color: 'var(--sw-white-color-90)', fontSize: '0.7rem', opacity: 0.5 }}
          >
            ·
          </Typography>
        )}
        {result.dateRange && (
          <Typography
            variant="caption"
            sx={{ color: 'var(--sw-white-color-90)', fontSize: '0.7rem' }}
          >
            {result.dateRange.start} → {result.dateRange.end}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function SearchRefinerCard({
  result,
  toolUseId,
}: {
  result: SearchRefinerResult;
  toolUseId: string;
}) {
  const hasTriggered = useRef(false);

  useEffect(() => {
    if (hasTriggered.current) return;
    if (!result.actionParams) return;
    if (triggeredToolCalls.has(toolUseId)) return;

    hasTriggered.current = true;
    triggeredToolCalls.add(toolUseId);
    triggerEventWithMessage('agent:refine-search', result.actionParams);
  }, [result.actionParams, toolUseId]);

  return (
    <Box
      sx={{
        mt: '4px',
        bgcolor: 'var(--tertiary-color)',
        border: '1px solid rgba(244, 246, 255, 0.1)',
        borderRadius: '8px',
        p: '0.625rem 0.75rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
      }}
    >
      <TuneIcon sx={{ fontSize: '0.875rem', color: 'var(--primary-color)' }} />
      <Typography variant="caption" sx={{ color: 'var(--sw-white-color)', fontSize: '0.75rem' }}>
        {result.summary || 'Search refined'}
      </Typography>
    </Box>
  );
}

function OrderFilterCard({
  result,
  toolUseId,
}: {
  result: SearchRefinerResult;
  toolUseId: string;
}) {
  const hasTriggered = useRef(false);

  useEffect(() => {
    if (hasTriggered.current) return;
    if (!result.actionParams) return;
    if (triggeredToolCalls.has(toolUseId)) return;

    hasTriggered.current = true;
    triggeredToolCalls.add(toolUseId);
    triggerEventWithMessage('agent:filter-orders', result.actionParams);
  }, [result.actionParams, toolUseId]);

  return (
    <Box
      sx={{
        mt: '4px',
        bgcolor: 'var(--tertiary-color)',
        border: '1px solid rgba(244, 246, 255, 0.1)',
        borderRadius: '8px',
        p: '0.625rem 0.75rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
      }}
    >
      <FilterListIcon sx={{ fontSize: '0.875rem', color: 'var(--primary-color)' }} />
      <Typography variant="caption" sx={{ color: 'var(--sw-white-color)', fontSize: '0.75rem' }}>
        {result.summary || 'Orders filtered'}
      </Typography>
    </Box>
  );
}

function CartManagerCard({ result, toolUseId }: { result: CartManagerResult; toolUseId: string }) {
  const hasTriggered = useRef(false);

  useEffect(() => {
    if (hasTriggered.current) return;
    if (!result.actionParams) return;
    if (triggeredToolCalls.has(toolUseId)) return;

    hasTriggered.current = true;
    triggeredToolCalls.add(toolUseId);
    triggerEventWithMessage('agent:cart-action', result.actionParams);
  }, [result.actionParams, toolUseId]);

  return (
    <Box
      sx={{
        mt: '4px',
        bgcolor: 'var(--tertiary-color)',
        border: '1px solid rgba(244, 246, 255, 0.1)',
        borderRadius: '8px',
        p: '0.625rem 0.75rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
      }}
    >
      <ShoppingCartIcon sx={{ fontSize: '0.875rem', color: 'var(--primary-color)' }} />
      <Typography variant="caption" sx={{ color: 'var(--sw-white-color)', fontSize: '0.75rem' }}>
        {result.summary || 'Cart updated'}
      </Typography>
    </Box>
  );
}

function PageNavigatorCard({
  result,
  toolUseId,
}: {
  result: PageNavigatorResult;
  toolUseId: string;
}) {
  const hasTriggered = useRef(false);

  useEffect(() => {
    if (hasTriggered.current) return;
    if (!result.actionParams) return;
    if (triggeredToolCalls.has(toolUseId)) return;

    hasTriggered.current = true;
    triggeredToolCalls.add(toolUseId);
    triggerEventWithMessage('agent:navigate-to', result.actionParams);
  }, [result.actionParams, toolUseId]);

  return (
    <Box
      sx={{
        mt: '4px',
        bgcolor: 'var(--tertiary-color)',
        border: '1px solid rgba(244, 246, 255, 0.1)',
        borderRadius: '8px',
        p: '0.625rem 0.75rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
      }}
    >
      <OpenInNewIcon sx={{ fontSize: '0.875rem', color: 'var(--primary-color)' }} />
      <Typography variant="caption" sx={{ color: 'var(--sw-white-color)', fontSize: '0.75rem' }}>
        {result.summary || 'Navigating...'}
      </Typography>
    </Box>
  );
}

function OfferingsBrowserCard({
  result,
  toolUseId,
}: {
  result: OfferingsBrowserResult;
  toolUseId: string;
}) {
  const hasTriggered = useRef(false);

  useEffect(() => {
    if (hasTriggered.current) return;
    if (!result.actionParams) return;
    if (triggeredToolCalls.has(toolUseId)) return;

    hasTriggered.current = true;
    triggeredToolCalls.add(toolUseId);
    triggerEventWithMessage('agent:navigate-to', result.actionParams);
  }, [result.actionParams, toolUseId]);

  return (
    <Box
      sx={{
        mt: '4px',
        bgcolor: 'var(--tertiary-color)',
        border: '1px solid rgba(244, 246, 255, 0.1)',
        borderRadius: '8px',
        p: '0.625rem 0.75rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
      }}
    >
      <FilterListIcon sx={{ fontSize: '0.875rem', color: 'var(--primary-color)' }} />
      <Typography variant="caption" sx={{ color: 'var(--sw-white-color)', fontSize: '0.75rem' }}>
        {result.summary || 'Filtering product library...'}
      </Typography>
    </Box>
  );
}

export default function ToolResultCard({ toolCall }: ToolResultCardProps) {
  const label = TOOL_LABELS[toolCall.toolName] || toolCall.toolName;
  const [expanded, setExpanded] = useState(false);

  // Render rich card for catalog_search results
  if (
    toolCall.toolName === 'catalog_search' &&
    toolCall.result &&
    typeof toolCall.result === 'object'
  ) {
    const result = toolCall.result as CatalogSearchResult;
    if (result.searchParams) {
      return <CatalogSearchCard result={result} toolUseId={toolCall.toolUseId} />;
    }
  }

  // Render rich card for search_refiner results
  if (
    toolCall.toolName === 'search_refiner' &&
    toolCall.result &&
    typeof toolCall.result === 'object'
  ) {
    const result = toolCall.result as SearchRefinerResult;
    if (result.actionParams) {
      return <SearchRefinerCard result={result} toolUseId={toolCall.toolUseId} />;
    }
  }

  // Render rich card for order_filter results
  if (
    toolCall.toolName === 'order_filter' &&
    toolCall.result &&
    typeof toolCall.result === 'object'
  ) {
    const result = toolCall.result as SearchRefinerResult;
    if (result.actionParams) {
      return <OrderFilterCard result={result} toolUseId={toolCall.toolUseId} />;
    }
  }

  // Render rich card for cart_manager results
  if (
    toolCall.toolName === 'cart_manager' &&
    toolCall.result &&
    typeof toolCall.result === 'object'
  ) {
    const result = toolCall.result as CartManagerResult;
    if (result.actionParams) {
      return <CartManagerCard result={result} toolUseId={toolCall.toolUseId} />;
    }
  }

  // Render rich card for page_navigator results
  if (
    toolCall.toolName === 'page_navigator' &&
    toolCall.result &&
    typeof toolCall.result === 'object'
  ) {
    const result = toolCall.result as PageNavigatorResult;
    if (result.actionParams) {
      return <PageNavigatorCard result={result} toolUseId={toolCall.toolUseId} />;
    }
  }

  // Render rich card for offerings_browser filter results (navigates to product library with filters)
  if (
    toolCall.toolName === 'offerings_browser' &&
    toolCall.result &&
    typeof toolCall.result === 'object'
  ) {
    const result = toolCall.result as OfferingsBrowserResult;
    if (result.actionParams) {
      return <OfferingsBrowserCard result={result} toolUseId={toolCall.toolUseId} />;
    }
  }

  // Default accordion for other tools

  const resultPreview = (() => {
    try {
      const str =
        typeof toolCall.result === 'string'
          ? toolCall.result
          : JSON.stringify(toolCall.result, null, 2);
      return str.length > 200 ? str.slice(0, 200) + '...' : str;
    } catch {
      return 'Result available';
    }
  })();

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      disableGutters
      sx={{
        mt: '4px',
        bgcolor: 'var(--tertiary-color)',
        border: '1px solid rgba(244, 246, 255, 0.1)',
        borderRadius: '8px !important',
        '&:before': { display: 'none' },
        boxShadow: 'none',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: 'var(--sw-white-color-90)', fontSize: '1rem' }} />}
        sx={{
          minHeight: 32,
          px: '0.75rem',
          '& .MuiAccordionSummary-content': { my: '4px' },
        }}
      >
        <Typography variant="caption" sx={{ color: 'var(--sw-white-color-90)' }}>
          {label} result
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: '0.75rem', pt: 0, pb: '0.5rem' }}>
        <Box
          component="pre"
          sx={{
            bgcolor: '#1a2530',
            borderRadius: '6px',
            p: 1,
            overflowX: 'auto',
            fontSize: '0.75rem',
            fontFamily: 'monospace',
            color: 'var(--sw-white-color)',
            m: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: 200,
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'var(--primary-color)', borderRadius: 2 },
          }}
        >
          {expanded
            ? typeof toolCall.result === 'string'
              ? toolCall.result
              : JSON.stringify(toolCall.result, null, 2)
            : resultPreview}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
